import { gql, useMutation } from '@apollo/client';
import { Button, Dialog, Select } from '@radix-ui/themes';
import { Calendar } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import CopyLink from '../../elements/CopyLink/CopyLink';
import './OpenApplicationDialog.css';

const extendOpenApplicationMutation = gql`
  mutation extendOpenApplicationSummary($applicationId: String!, $date: Date!) {
    extendOpenApplicationSummary(applicationId: $applicationId, date: $date) {
      __typename

      ... on OpenApplicationSummary {
        expiry
      }

      ... on ExtendOpenApplicationSummaryError {
        code
      }
    }
  }
`;

const toDisplayString = (date: number) =>
  `${new Date(date).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })} ${new Date(date).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;

const expiresWithinAWeek = (date: number) => date - Date.now() > 0 && date - Date.now() <= 1000 * 60 * 60 * 24 * 7;
const isExpired = (date: number) => Date.now() > date;

const OpenApplicationDialog = ({
  show,
  onHide,
  openApplication,
  applicationId,
}: {
  show: boolean;
  onHide: () => void;
  openApplication: { expiry: number };
  applicationId: string;
}) => {
  const { t } = useTranslation();
  const { expiry } = openApplication;

  const [extendSummaryLinkError, setExtendSummaryLinkError] = useState('');
  const expired = isExpired(expiry);
  const isExpiringWithinAWeek = expiresWithinAWeek(expiry);
  const displayDate = toDisplayString(expiry);

  const [extendOpenApplication, { loading: extendOpenApplicationLoading }] = useMutation(
    extendOpenApplicationMutation,
    {
      update(cache, { data: { extendOpenApplicationSummary } }) {
        if (extendOpenApplicationSummary.expiry) {
          cache.modify({
            id: cache.identify({ __typename: 'Application', id: applicationId }),
            fields: {
              openApplicationSummary() {
                return {
                  isDisabled: true,
                  expiry: extendOpenApplicationSummary.expiry,
                };
              },
            },
          });

          const newExpiryDate = new Date(extendOpenApplicationSummary.expiry);

          setExtendSummaryLinkError('');
          toast.success(`The open summary has been extended until ${newExpiryDate.toLocaleString()}.`);
        } else if (extendOpenApplicationSummary.code === 'EXPIRY_DATE_TOO_FAR') {
          setExtendSummaryLinkError(t('swiper.openApplication.extendError'));
        }
      },
    },
  );

  const [extendDuration, setExtendDuration] = useState('1h');

  const handleDuration = (duration: string) => {
    if (duration !== '') {
      setExtendDuration(duration);
    }
  };
  const handleExtendOpenApplication = async () => {
    const regex = /^(\d+)([dh])$/i; // 1d, 1h
    const match = extendDuration.match(regex);
    const now = new Date();

    if (!match) {
      throw new Error('Invalid duration format.');
    }

    const amount = parseInt(match[1], 10);
    const unit = match[2].toLowerCase();

    let futureDate = new Date(expiry);

    if (futureDate.getTime() < now.getTime()) {
      futureDate = now;
    }

    switch (unit) {
      case 'd':
        futureDate.setDate(futureDate.getDate() + amount);
        break;
      case 'h':
        futureDate.setHours(futureDate.getHours() + amount);
        break;
      default:
        throw new Error('Invalid duration unit.');
    }

    await extendOpenApplication({
      variables: {
        applicationId,
        date: futureDate.toISOString(),
      },
    });
  };

  return (
    <Dialog.Root open={show} onOpenChange={() => onHide()}>
      <Dialog.Content>
        <Dialog.Title>{t('swiper.openApplication.dialog.title')}</Dialog.Title>
        <Dialog.Description>{t('swiper.openApplication.dialog.description')}</Dialog.Description>
        <CopyLink
          className=""
          url={`${window.location.protocol}//${window.location.host}/application/share/${applicationId}`}
        />
        <hr />
        <div className="flex">
          <div className="w-1/2">
            <h3>{t('swiper.openApplication.expirationDateHeading')}</h3>
            <div className="open-summary-details-wrapper mb-4">
              <div
                className={`flex flex-row open-summary-details-text ${expired ? 'expired' : ''} ${
                  isExpiringWithinAWeek ? 'expires-soon' : ''
                }`}
              >
                <Calendar className="mr-2" size={24} />
                <span className="align-middle">{displayDate}</span>
              </div>
              {isExpiringWithinAWeek && (
                <span className="error-message warning">{t('swiper.openApplication.willExpireSoon')}</span>
              )}
              {expired && <div className="error-message expired">{t('swiper.openApplication.hasExpired')}</div>}
            </div>
          </div>
          <div className="w-1/2">
            <h3>{t('swiper.openApplication.extendHeading')}</h3>
            <Select.Root size="3" value={extendDuration} onValueChange={handleDuration}>
              <Select.Trigger radius="large" color="iris" />
              <Select.Content color="iris">
                <Select.Item value="1h">{t('swiper.openApplication.duration.oneHour')}</Select.Item>
                <Select.Item value="6h">{t('swiper.openApplication.duration.sixHours')}</Select.Item>
                <Select.Item value="12h">{t('swiper.openApplication.duration.twelveHours')}</Select.Item>
                <Select.Item value="1d">{t('swiper.openApplication.duration.oneDay')}</Select.Item>
                <Select.Item value="7d">{t('swiper.openApplication.duration.sevenDays')}</Select.Item>
                <Select.Item value="30d">{t('swiper.openApplication.duration.thirtyDays')}</Select.Item>
                <Select.Item value="90d">{t('swiper.openApplication.duration.ninetyDays')}</Select.Item>
              </Select.Content>
            </Select.Root>
            <div className="open-summary__extend-error mb-4 ml-4">{extendSummaryLinkError}</div>
          </div>
        </div>
        <Button color="iris" size="3" loading={extendOpenApplicationLoading} onClick={handleExtendOpenApplication}>
          {t('swiper.openApplication.addMoreTimeButton')}
        </Button>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default OpenApplicationDialog;
